import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';

import { createSuperAdminUser } from '@actions/superAdminUsers/postCreateSuperAdminUser';
import {
    getSuperAdminUserIsPosting,
    getSuperAdminUserPostSuccess,
    getSuperAdminUsersError,
} from '@selectors/superAdminUsers';
import { useHistory } from 'react-router-dom';
import { CloseModal, HandleSubmit } from 'src/types/shared/Functions';
import useSite from 'lib/src/hooks/useSite';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { site as siteEnum } from 'lib/src/shared/enums/site';

export default function useCreateSuperAdmin(): UseCreateSuperAdminReturn {
    const dispatch = useDispatch();
    const history = useHistory();

    const isPosting = useSelector(getSuperAdminUserIsPosting);
    const postSuccess = useSelector(getSuperAdminUserPostSuccess);
    const error = useSelector(getSuperAdminUsersError);
    const site = useSite();
    const prevProps = usePrevious({ postSuccess, site });
    const initialFormData: FormState = {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        site: site ?? siteEnum.IX,
    };

    const [formState, handleChange] = useForm(initialFormData);

    useEffect(() => {
        if (!!site && site !== prevProps.site) {
            handleChange('site', site);
        }
    }, [site, handleChange, prevProps.site]);

    const handleSubmit = useCallback(() => {
        dispatch(createSuperAdminUser(formState));
    }, [dispatch, formState]);

    const closeModal = useCallback(() => {
        history.push('/superadmins');
    }, [history]);

    useEffect(() => {
        if (!prevProps.site && postSuccess) {
            closeModal();
        }
    }, [prevProps.site, postSuccess, closeModal]);

    const siteOptions: DropdownOption<siteEnum>[] = [
        { label: 'All', value: siteEnum.ALL },
        { label: 'IX', value: siteEnum.IX },
        { label: 'GA', value: siteEnum.GA },
    ];
    const shouldShowSiteOptions = site === siteEnum.ALL;

    return {
        isPosting,
        error,
        formState,
        handleChange,
        handleSubmit,
        closeModal,
        siteOptions,
        shouldShowSiteOptions,
    };
}

interface FormState {
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    site: number | null;
}

interface UseCreateSuperAdminReturn {
    isPosting: boolean;
    error: string | null;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    handleSubmit: HandleSubmit;
    closeModal: CloseModal;
    siteOptions: DropdownOption<siteEnum>[];
    shouldShowSiteOptions: boolean;
}
